// =============================================================================
// SaaS-Admin › Payments
// Live data from GET /saastracker/admin/payments/queue + /admin/transactions.
//
// This is the TRANSACTIONS table — PayPal orders and what became of them. The
// mock modelled it as a dunning queue with retry counts and next-retry dates,
// and none of that exists: prepaid top-ups are one-shot PayPal checkouts that
// are never retried, so there is nothing to dun. What actually accumulates is
// abandoned checkouts — someone opened the PayPal window and closed it.
//
// Statuses are TransactionStatus: Created → Pending → Completed, or Failed /
// Refunded. Created and Pending are the unfinished ones.
// =============================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../services/adminDataService';

const PAGE_SIZE = 25;

const STATUS_TABS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  // Cuts across TransactionStatus rather than being one of them: charged but not
  // credited is usually Pending, but a refused grant leaves it Completed.
  { key: 'review', label: 'Needs review' },
  { key: 'Completed', label: 'Completed' },
  { key: 'Pending', label: 'Awaiting capture' },
  { key: 'Created', label: 'Never approved' },
  { key: 'Failed', label: 'Failed' },
  { key: 'Refunded', label: 'Refunded' },
];

// ---- formatting -------------------------------------------------------------

const fmtMoney = (n: number | null | undefined, ccy: string = 'USD'): string =>
  `${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

const fmtCredits = (n: number | null | undefined): string =>
  (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const fmtAge = (hours: number): string => {
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const isUnfinished = (s: api.TransactionStatus) => s === 'Created' || s === 'Pending';

// Inline rather than in saas-admin.css: this is a small, self-contained addition
// and it keeps the exception styling next to the logic that decides to show it.
const REVIEW_PILL: React.CSSProperties = {
  display: 'inline-block', marginLeft: 6, padding: '2px 8px', borderRadius: 999,
  background: '#fdecea', color: '#c0392b', border: '1px solid #f5c6cb',
  fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
};

const REVIEW_ROW: React.CSSProperties = { borderLeft: '3px solid #c0392b', background: '#fffbfb' };

const REVIEW_BANNER: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  margin: '0 0 14px', padding: '11px 14px', borderRadius: 8,
  background: '#fdecea', color: '#96281b', border: '1px solid #f5c6cb',
  fontSize: 13, fontWeight: 500,
};

// ---- page -------------------------------------------------------------------

export const PaymentCollection: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  const [queue, setQueue] = useState<api.AdminPaymentQueueResponse | null>(null);
  const [data, setData] = useState<api.AdminTransactionListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [reconciling, setReconciling] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  const flash = useCallback((m: string) => {
    setErr(''); setMsg(m); window.setTimeout(() => setMsg(''), 6000);
  }, []);
  const fail = useCallback((e: any) => {
    setMsg(''); setErr(e?.message ?? String(e));
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!showHelp) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowHelp(false); };
    const onDown = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) setShowHelp(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [showHelp]);

  const reqId = useRef(0);
  const queueReqId = useRef(0);

  /**
   * The summary cards and the review banner. Their counts cover ALL transactions, so
   * they do not change with the table's filter, search or page: loaded when the page
   * opens and on Refresh, Retry or after Reconcile, not on every table change.
   */
  const loadQueue = useCallback(async () => {
    const id = ++queueReqId.current;
    try {
      const q = await api.getPaymentQueue();
      if (id === queueReqId.current) setQueue(q);
    } catch (e) {
      if (id === queueReqId.current) fail(e);
    }
  }, [fail]);

  /** One page of the transaction table for the current filter, search and page. */
  const loadTransactions = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setErr('');
    try {
      const t = await api.getTransactions({ status, search, page, pageSize: PAGE_SIZE });
      if (id !== reqId.current) return;
      setData(t);
    } catch (e) {
      if (id === reqId.current) { setData(null); fail(e); }
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [status, search, page, fail]);

  /** Full refresh of cards and table (Refresh, Retry, after Reconcile). */
  const load = useCallback(async () => {
    await Promise.all([loadQueue(), loadTransactions()]);
  }, [loadQueue, loadTransactions]);

  useEffect(() => { loadQueue(); }, [loadQueue]);
  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  /**
   * Repairs captures that never got stamped Completed. Safe by construction — it
   * only touches transactions that already have a granted PayPal purchase on the
   * ledger — so no confirmation step is needed.
   */
  const reconcile = async () => {
    setReconciling(true);
    try {
      const r = await api.reconcilePayments();
      flash(r.repaired === 0
        ? `Scanned ${r.scanned} unfinished transaction${r.scanned === 1 ? '' : 's'} — none had a captured payment to recover.`
        : `Repaired ${r.repaired} settlement${r.repaired === 1 ? '' : 's'}` +
          (r.invoicesCreated ? `, raised ${r.invoicesCreated} missing invoice${r.invoicesCreated === 1 ? '' : 's'}.` : '.'));
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setReconciling(false);
    }
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstOnPage = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="ov-page">
      <header className="ov-header">
        <div>
          <h1 className="ov-header__title">Payments</h1>
          <p className="ov-header__sub">
            Every PayPal transaction — completed top-ups and the ones that never finished.
          </p>
        </div>
        <div className="ov-header__actions">
          <button className="btn-small" onClick={load} disabled={loading}>
            <span className="material-icons">refresh</span> Refresh
          </button>
          <button className="btn-small btn-small--primary" onClick={reconcile} disabled={reconciling}>
            <span className="material-icons">build</span>
            {reconciling ? 'Reconciling…' : 'Reconcile settlements'}
          </button>

          <div className="rem-help" ref={helpRef}>
            <button
              type="button" className="rem-help__btn" aria-expanded={showHelp}
              aria-label="How this page works" title="How this page works"
              onClick={() => setShowHelp(v => !v)}
            >
              <span className="material-icons">help_outline</span>
            </button>
            {showHelp && (
              <div className="rem-help__panel" role="dialog" aria-label="How this page works">
                <button
                  type="button" className="rem-help__close" aria-label="Close"
                  onClick={() => setShowHelp(false)}
                >
                  <span className="material-icons">close</span>
                </button>
                <p>
                  This is the transaction ledger, not a dunning queue. Prepaid top-ups are
                  one-shot PayPal checkouts — nothing is ever retried — so the unfinished
                  rows are mostly <strong>abandoned checkouts</strong>: someone opened the
                  PayPal window and closed it. They are harmless, and they accumulate.
                </p>
                <p>
                  A row is created the moment the PayPal button is pressed
                  (<strong>Created</strong>), moves to <strong>Awaiting capture</strong> once
                  the order exists, and reaches <strong>Completed</strong> when the capture
                  settles and credit is granted.
                </p>
                <p>
                  <strong>Needs review</strong> is the one that costs money: PayPal captured the
                  payment and credits were deliberately not granted — the captured amount did not
                  match the order, or the grant itself was refused. The row keeps the reason and
                  the capture id, so it can be refunded or granted by hand. Everything else in
                  this list is a checkout nobody paid for.
                </p>
                <p>
                  <strong>Reconcile settlements</strong> repairs transactions PayPal really
                  did capture but that were never stamped Completed. It only touches rows
                  that already carry a granted purchase on the credit ledger, so it cannot
                  invent a payment, and it never calls PayPal.
                </p>
              </div>
            )}
          </div>
        </div>
      </header>

      {(msg || err) && (
        <div style={{
          margin: '0 0 14px', padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
          background: err ? '#fdecea' : '#e7f6ec', color: err ? '#c0392b' : '#1e7e46',
          border: `1px solid ${err ? '#f5c6cb' : '#bfe6cd'}`,
        }}>
          {err || msg}
          {err && <button className="btn-small" style={{ marginLeft: 10 }} onClick={load}>Retry</button>}
        </div>
      )}

      {/* The one condition on this page that has already cost a customer money. */}
      {queue && queue.needsReview > 0 && (
        <div style={REVIEW_BANNER}>
          <span className="material-icons">report_problem</span>
          <span>
            <strong>
              {queue.needsReview} payment{queue.needsReview === 1 ? ' was' : 's were'} captured
              without granting credit
            </strong>{' '}
            — {fmtMoney(queue.amountNeedingReview)} taken with nothing given in return. These need a
            refund or a manual grant.
          </span>
          <button
            className="btn-small"
            style={{ marginLeft: 'auto' }}
            onClick={() => { setStatus('review'); setPage(1); }}
          >
            Show them
          </button>
        </div>
      )}

      {queue && (
        <section className="ov-kpis">
          <div className="kpi">
            <div className="kpi__icon kpi__icon--warn"><span className="material-icons">hourglass_disabled</span></div>
            <div className="kpi__main">
              <div className="kpi__num">{queue.abandoned.toLocaleString('en-US')}</div>
              <div className="kpi__label">Abandoned</div>
              <div className="kpi__sub">open more than 24h</div>
            </div>
          </div>
          <div className="kpi">
            <div className="kpi__icon kpi__icon--blue"><span className="material-icons">schedule</span></div>
            <div className="kpi__main">
              <div className="kpi__num">{queue.pending.toLocaleString('en-US')}</div>
              <div className="kpi__label">Awaiting capture</div>
              <div className="kpi__sub">{fmtMoney(queue.amountAtRisk)} still in flight</div>
            </div>
          </div>
          <div className="kpi">
            <div className="kpi__icon kpi__icon--warn"><span className="material-icons">error_outline</span></div>
            <div className="kpi__main">
              <div className="kpi__num">{queue.failed.toLocaleString('en-US')}</div>
              <div className="kpi__label">Failed</div>
              <div className="kpi__sub">declined by PayPal</div>
            </div>
          </div>
          <div className="kpi">
            <div className="kpi__icon kpi__icon--purple"><span className="material-icons">undo</span></div>
            <div className="kpi__main">
              <div className="kpi__num">{queue.refunded.toLocaleString('en-US')}</div>
              <div className="kpi__label">Refunded</div>
              <div className="kpi__sub">credit clawed back</div>
            </div>
          </div>
        </section>
      )}

      <div className="filters">
        <div className="filters__search">
          <span className="material-icons">search</span>
          <input
            type="text"
            placeholder="Search by tenant, order id or capture id…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>
        <button
          className="btn-small btn-small--ghost"
          onClick={() => { setSearchInput(''); setSearch(''); setStatus('all'); setPage(1); }}
        >
          Reset
        </button>
      </div>

      <div className="invoice-tabs">
        {STATUS_TABS.map(t => (
          <button
            key={t.key}
            className={`invoice-tab${status === t.key ? ' invoice-tab--active' : ''}`}
            onClick={() => { setStatus(t.key); setPage(1); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="ov-section" style={{ padding: 0 }}>
        <div className="pay-table">
          <div className="pay-table__head">
            <div>Started</div><div>Tenant</div><div>Amount</div><div>Credits</div>
            <div>PayPal reference</div><div>Status</div><div>Action</div>
          </div>

          {items.map(t => (
            <div
              className={`pay-row${isUnfinished(t.status) && t.ageHours >= 24 ? ' pay-row--stale' : ''}`}
              style={t.needsReview ? REVIEW_ROW : undefined}
              key={t.id}
            >
              <div className="pay-row__when">
                {fmtDateTime(t.createdAt)}
                <em>{fmtAge(t.ageHours)}</em>
              </div>

              <div className="pay-row__tenant">
                <span className="pay-row__name">{t.tenantName}</span>
                <em>{t.tenantEmail || 'no email on file'}</em>
                {t.needsReview && t.failureReason && (
                  <em style={{ color: '#c0392b', fontStyle: 'normal', fontWeight: 500 }}>
                    {t.failureReason}
                  </em>
                )}
              </div>

              <div className="pay-row__amount">{fmtMoney(t.amount, t.currency)}</div>
              <div className="pay-row__num">{fmtCredits(t.credits)}</div>

              <div className="pay-row__ref">
                <span title={t.orderId ?? ''}>{t.orderId || '—'}</span>
                <em title={t.captureId ?? t.failureCaptureId ?? ''}>
                  {t.captureId
                    ? `capture ${t.captureId}`
                    : t.failureCaptureId
                      ? `captured ${t.failureCaptureId} — not credited`
                      : 'not captured'}
                </em>
              </div>

              <div>
                <span className={`tx-status tx-status--${t.status.toLowerCase()}`}>{t.status}</span>
                {t.invoiced && (
                  <span className="pay-row__invoiced" title="A receipt was raised for this payment">
                    <span className="material-icons">receipt_long</span>
                  </span>
                )}
                {t.needsReview && (
                  <span
                    style={REVIEW_PILL}
                    title={t.failureReason ?? 'PayPal took the money; credits were not granted.'}
                  >
                    Needs review
                  </span>
                )}
              </div>

              <div>
                {t.tenantEmail && (t.status !== 'Completed' || t.needsReview) ? (
                  <a
                    className="btn-small"
                    href={`mailto:${t.tenantEmail}?subject=${encodeURIComponent('Your Logipulse top-up')}`}
                    title={`Email ${t.tenantName}`}
                  >
                    <span className="material-icons">mail</span> Contact
                  </a>
                ) : (
                  <span className="pay-row__none">—</span>
                )}
              </div>
            </div>
          ))}

          {loading && items.length === 0 && (
            <div className="empty">
              <span className="material-icons">hourglass_empty</span>
              Loading transactions…
            </div>
          )}

          {!loading && items.length === 0 && !err && (
            <div className="empty">
              <span className="material-icons">inbox</span>
              No transactions match these filters.
            </div>
          )}

          {!loading && err && (
            <div className="empty">
              <span className="material-icons">cloud_off</span>
              Could not reach the SaaS Tracker API.
              <button className="btn-small" onClick={load}>Retry</button>
            </div>
          )}
        </div>
      </div>

      {total > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 12, fontSize: 12, color: '#6b7684',
        }}>
          <span>Showing {firstOnPage}–{lastOnPage} of {total.toLocaleString('en-US')}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn-small" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>
              <span className="material-icons">chevron_left</span> Prev
            </button>
            <span>Page {page} of {pageCount}</span>
            <button className="btn-small" disabled={page >= pageCount || loading} onClick={() => setPage(p => p + 1)}>
              Next <span className="material-icons">chevron_right</span>
            </button>
          </span>
        </div>
      )}
    </div>
  );
};
